import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../../text';
import {utils} from '../../utils';
import {svg} from '../../assets/svg';
import {theme} from '../../constants';
import {components} from '../../components';
import {hooks} from '../../hooks';
import {custom} from '../../custom';
import {RootState} from '../../store';
import {socketService} from '../../services';
import {formatDateTime} from '../../utils/formatText';
import axiosInstance from '../../services/axiosInstance';
import { MdFilterList } from 'react-icons/md';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLanguage } from '../../context/LanguageContext';
import { listCache } from '../../utils/listCache';

const FilterListIcon = MdFilterList as any;

const abbreviateName = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return name;
  // Jika salah satu dari kata pertama/kedua < 5 karakter, tampilkan keduanya penuh
  const keepBothFull = parts[0].length < 5 || parts[1].length < 5;
  const prefix = keepBothFull ? parts[0] + ' ' + parts[1] : parts[0] + ' ' + parts[1][0].toUpperCase() + '.';
  if (parts.length === 2) return keepBothFull ? name : prefix;
  // Kata ketiga dan seterusnya selalu disingkat
  const rest = parts.slice(2).map(p => p[0].toUpperCase() + '.').join(' ');
  return prefix + ' ' + rest;
};

type Task = {
  id: string;
  parent_task_id?: number | null;
  task_type: string;
  name?: string;
  description: string;
  created_at: string;
  updated_at?: string;
  end_date?: string | null;
  total_duration?: number;
  status: string;
  is_urgent?: boolean;
  is_new?: boolean;
  has_update?: boolean;
  has_new_comments?: boolean;
  _commentCount?: number;
  is_comment_update?: boolean;
  assigned_by: {
    id: string;
    name: string;
    email: string;
    group: string;
  };
  assigned_to: {
    id: string;
    name: string;
    email: string;
    group: string;
  }[];
  checker?: {
    id: string | number;
    name: string;
  };
  score_badge?: {
    score: number;
    color: string;
  };
  has_pending_sync?: boolean;
};

// Countdown timer isolated in its own component so only it re-renders every second,
// not the entire Task screen (which would dismiss the virtual keyboard on Android).
const CountdownTimer: React.FC<{ endDateString: string; status: string; overduLabel: string }> = React.memo(
  ({ endDateString, status, overduLabel }) => {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
      const id = setInterval(() => setNow(new Date()), 1000);
      return () => clearInterval(id);
    }, []);

    if (status?.toLowerCase() === 'on_hold' || status?.toLowerCase() === 'on hold') {
      return <span style={{ color: '#F59E0B', fontWeight: '600', fontSize: 12 }}>⏸ On Hold</span>;
    }

    const endDate = new Date(endDateString);
    if (isNaN(endDate.getTime())) return null;

    const diff = endDate.getTime() - now.getTime();
    if (diff <= 0) {
      return (
        <span style={{ color: '#EF4444', fontWeight: 'bold', animation: 'blink 1s linear infinite', fontSize: 12 }}>
          {overduLabel}
        </span>
      );
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / 1000 / 60) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    let str = '';
    if (days > 0) str += `${days}d `;
    if (hours > 0 || days > 0) str += `${hours}h `;
    if (minutes > 0 || hours > 0 || days > 0) str += `${minutes}m `;
    str += `${seconds}s`;

    return <span style={{ color: '#F59E0B', fontWeight: '600', fontSize: 12 }}>⏱ {str}</span>;
  }
);

// ============================================================
// TaskListBody — owns ALL task data state so that fetches and
// socket updates never cause the parent (search input) to re-render.
// ============================================================
type TaskListBodyProps = {
  debouncedSearchTerm: string;
  statusFilter: string;
  // Refs carry live values without triggering re-renders from typing
  searchTermRef: React.MutableRefObject<string>;
  t: any;
  onCountsUpdate: (counts: Record<string, number>) => void;
  onRestoreFilters: (search: string, status: string) => void;
};

const TaskListBody: React.FC<TaskListBodyProps> = React.memo(({
  debouncedSearchTerm,
  statusFilter,
  searchTermRef,
  t,
  onCountsUpdate,
  onRestoreFilters,
}) => {
  const navigate = hooks.useAppNavigate();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const restoredFromStorage = useRef(false);
  const fetchTriggerRef = useRef<string>('');

  // Refs for unmount-save (always hold latest values without causing re-renders)
  const tasksRef = useRef<Task[]>([]);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const statusFilterRef = useRef(statusFilter);
  const userIdRef = useRef(authSlice.userId);
  const navigatedToTaskRef = useRef(false); // true = user went to task detail, skip unmount save

  // Keep refs in sync with latest state/props every render
  tasksRef.current = tasks;
  pageRef.current = page;
  hasMoreRef.current = hasMore;
  statusFilterRef.current = statusFilter;
  userIdRef.current = authSlice.userId;

  const fetchTaskCounts = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/tasks/counts');
      onCountsUpdate(response.data);
    } catch (error) {
      console.error('Failed to fetch task counts:', error);
    }
  }, [onCountsUpdate]);

  const refreshTasksInPlace = useCallback(async (searchStr = '', statusStr = 'all', maxPage = 1) => {
    try {
      const loadedLimit = maxPage * 5;
      let url = `/tasks?page=1&limit=${loadedLimit}`;
      if (searchStr) url += `&search=${encodeURIComponent(searchStr)}`;
      if (statusStr && statusStr !== 'all') url += `&status=${encodeURIComponent(statusStr)}`;
      const response = await axiosInstance.get(url);
      const freshTasks: Task[] = response.data || [];
      setTasks(prev => {
        const freshMap = new Map(freshTasks.map(t => [t.id, t]));
        const prevIds = new Set(prev.map(t => t.id));
        const newOnes = freshTasks.filter(t => !prevIds.has(t.id));
        const updated = prev
          .filter(t => freshMap.has(t.id))
          .map(t => freshMap.get(t.id) as Task);
        return [...newOnes, ...updated];
      });
      setHasMore(freshTasks.length === loadedLimit);
    } catch (err) {
      console.error('Background refresh error:', err);
    }
  }, []);

  const fetchTasks = useCallback(async (pageNumber: number, isRefresh = false, searchStr = '', statusStr = 'all') => {
    try {
      if (pageNumber === 1) setIsLoading(true);
      else setIsFetchingMore(true);
      setError('');

      let url = `/tasks?page=${pageNumber}&limit=5`;
      if (searchStr) url += `&search=${encodeURIComponent(searchStr)}`;
      if (statusStr && statusStr !== 'all') url += `&status=${encodeURIComponent(statusStr)}`;

      const response = await axiosInstance.get(url);
      const newTasks = response.data || [];

      if (isRefresh) {
        setTasks(newTasks);
      } else {
        setTasks((prev) => {
          const existingIds = new Set(prev.map(t => t.id));
          const uniqueNewTasks = newTasks.filter((t: Task) => !existingIds.has(t.id));
          return [...prev, ...uniqueNewTasks];
        });
      }
      setHasMore(newTasks.length === 5);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch tasks';
      setError(errorMsg);
      console.error('Error fetching tasks:', err);
    } finally {
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  }, []);

  // Restore scroll position & task list when navigating back from task detail
  useEffect(() => {
    const cached = listCache.restore('tasks');
    if (!cached) return;

    // Buang cache jika milik user/tenant berbeda (misalnya setelah logout & login ulang)
    if (cached.meta?.userId !== undefined && String(cached.meta.userId) !== String(authSlice.userId)) {
      listCache.clear('tasks');
      return;
    }

    if (cached.meta?.statusFilter || cached.meta?.searchTerm) {
      onRestoreFilters(cached.meta?.searchTerm || '', cached.meta?.statusFilter || 'all');
    }

    setTasks(cached.items);
    setPage(cached.cursor);
    setHasMore(cached.hasMore);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById('scrollableDiv');
        if (el) el.scrollTop = cached.scrollTop;
      });
    });

    fetchTriggerRef.current = `${cached.meta?.searchTerm || ''}|${cached.meta?.statusFilter || 'all'}`;
    restoredFromStorage.current = true;

    if (cached.needsRefresh) {
      // Saat offline: jangan clear cache agar navigasi berikutnya tetap punya fallback
      if (!navigator.onLine) return;
      listCache.clear('tasks');
      refreshTasksInPlace(cached.meta?.searchTerm || '', cached.meta?.statusFilter || 'all', cached.cursor);
      fetchTaskCounts();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Simpan cache saat unmount (pindah tab via bottom menu)
  // Tidak overwrite jika user memang navigasi ke task detail (sudah save dengan needsRefresh: true)
  useEffect(() => {
    return () => {
      if (navigatedToTaskRef.current || tasksRef.current.length === 0) return;
      const el = document.getElementById('scrollableDiv');
      listCache.save('tasks', {
        items: tasksRef.current,
        cursor: pageRef.current,
        hasMore: hasMoreRef.current,
        scrollTop: el?.scrollTop || 0,
        meta: { statusFilter: statusFilterRef.current, searchTerm: searchTermRef.current, userId: userIdRef.current },
        needsRefresh: false,
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial load / search / filter change
  useEffect(() => {
    if (authSlice.userId) {
      const currentKey = `${debouncedSearchTerm}|${statusFilter}`;

      if (restoredFromStorage.current) {
        listCache.clear('tasks');
        fetchTaskCounts();
        if (currentKey === fetchTriggerRef.current) {
          restoredFromStorage.current = false;
        }
        return;
      }

      if (fetchTriggerRef.current === currentKey) return;

      fetchTriggerRef.current = currentKey;
      setPage(1);
      fetchTasks(1, true, debouncedSearchTerm, statusFilter);
      fetchTaskCounts();
    }
  }, [authSlice.userId, debouncedSearchTerm, statusFilter, fetchTasks, fetchTaskCounts, refreshTasksInPlace]);

  // Refresh saat app kembali ke foreground
  useEffect(() => {
    const lastHiddenRef = { current: 0 };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenRef.current = Date.now();
      } else if (document.visibilityState === 'visible') {
        const hiddenDuration = Date.now() - lastHiddenRef.current;
        if (lastHiddenRef.current > 0 && hiddenDuration > 10_000) {
          refreshTasksInPlace(debouncedSearchTerm, statusFilter, page);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [debouncedSearchTerm, statusFilter, page, refreshTasksInPlace]);

  // WebSocket Integration
  useEffect(() => {
    const socketUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';
    socketService.connect(socketUrl, authSlice.accessToken || undefined);

    const handleTaskCreated = (newTask: any) => {
      fetchTaskCounts();
      const permissions = (authSlice as any).permissions || [];
      const canViewAllTasks = permissions.includes('task:view_all');
      const userId = authSlice.userId;
      const taskWithStatus = { ...newTask, is_new: true };

      if (canViewAllTasks) {
        setTasks((prev) => [taskWithStatus, ...prev]);
        return;
      }

      if (newTask.assigned_by_user_id == userId || (newTask.assigned_to_user_ids && newTask.assigned_to_user_ids.includes(parseInt(userId as any))) || newTask.checker_user_id == userId) {
        setTasks((prev) => [taskWithStatus, ...prev]);
      }
    };

    const handleTaskUpdated = (updatedTask: any) => {
      fetchTaskCounts();
      setTasks((prev) => {
        const permissions = (authSlice as any).permissions || [];
        const canViewAllTasks = permissions.includes('task:view_all');
        const userId = authSlice.userId;

        const existingTask = prev.find((t) => t.id === updatedTask.id);

        const isRelevant =
          canViewAllTasks ||
          (updatedTask.assigned_by_user_id ?? existingTask?.assigned_by?.id) == userId ||
          ((updatedTask.assigned_to_user_ids || existingTask?.assigned_to?.map((u:any) => u.id)) || []).includes(parseInt(userId as any)) ||
          (updatedTask.checker_user_id ?? existingTask?.checker?.id) == userId;

        let isNewCommentFromOther = false;
        let isNewestCommentFromMe = false;
        let currentCommentCount = 0;
        if (updatedTask.feedbacks) {
          let newestComment: any = null;
          updatedTask.feedbacks.forEach((f: any) => {
            f.comments?.forEach((c: any) => {
              currentCommentCount++;
              if (!newestComment || new Date(c.created_at) > new Date(newestComment.created_at)) {
                newestComment = c;
              }
            });
          });

          if (newestComment) {
            if (newestComment.user_id == userId) {
              isNewestCommentFromMe = true;
            } else {
              if (existingTask && existingTask._commentCount !== undefined) {
                if (currentCommentCount > existingTask._commentCount) {
                  isNewCommentFromOther = true;
                }
              } else if (updatedTask.is_comment_update) {
                isNewCommentFromOther = true;
              } else {
                const diff = Math.abs(new Date().getTime() - new Date(newestComment.created_at).getTime());
                if (diff < 300000) {
                  isNewCommentFromOther = true;
                }
              }
            }
          }
        }

        const exists = !!existingTask;

        if (exists) {
          if (!isRelevant) {
            return prev.filter((t) => t.id !== updatedTask.id);
          }
          return prev.map((t) => {
            if (t.id === updatedTask.id) {
              let updatedHasNewComments = t.has_new_comments;
              if (isNewestCommentFromMe) {
                updatedHasNewComments = false;
              } else if (isNewCommentFromOther) {
                updatedHasNewComments = true;
              }
              return { ...t, ...updatedTask, has_pending_sync: false, is_new: isNewestCommentFromMe ? false : t.is_new, has_update: isNewestCommentFromMe ? false : !t.is_new, has_new_comments: updatedHasNewComments, _commentCount: currentCommentCount };
            }
            return t;
          });
        } else {
          if (isRelevant) {
            let taskToAdd = { ...updatedTask, is_new: true, has_update: false, has_new_comments: isNewCommentFromOther, _commentCount: currentCommentCount };
            if (taskToAdd.assigned_to_user_ids?.includes(parseInt(userId as any)) && !taskToAdd.assigned_to) {
              taskToAdd.assigned_to = [{
                id: userId,
                name: authSlice.userName || 'Me',
                email: authSlice.email,
                group: (authSlice as any).groupName || 'Unknown'
              }];
            }
            return [taskToAdd, ...prev];
          }
        }
        return prev;
      });
    };

    socketService.on('task_created', handleTaskCreated);
    socketService.on('task_updated', handleTaskUpdated);

    return () => {
      socketService.off('task_created', handleTaskCreated);
      socketService.off('task_updated', handleTaskUpdated);
    };
  }, [authSlice]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAsRead = async (taskId: string) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? {...t, is_new: false, has_update: false, has_new_comments: false} : t)));
    try {
      await axiosInstance.post(`/tasks/${taskId}/read`);
    } catch (error) {
      console.error('Failed to mark task as read:', error);
    }
  };

  const truncateText = (text: string, length: number): string => {
    if (!text) return '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  const getStatusConfig = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || '';
    switch (normalizedStatus) {
      case 'draft':
        return {bg: '#E0F7FA', color: '#00BCD4', label: 'Draft'};
      case 'approved':
        return {bg: '#E3F2FD', color: '#2196F3', label: 'Approved'};
      case 'on_hold':
      case 'on hold':
        return {bg: '#FFF3E0', color: '#FF9800', label: 'Pending'};
      case 'in_progress':
      case 'in progress':
        return {bg: '#E0F2FE', color: '#2563EB', label: 'In Progress'};
      case 'in_review':
      case 'in review':
        return {bg: '#F3E5F5', color: '#9C27B0', label: 'In Review'};
      case 'completed':
        return {bg: '#E8F5E9', color: '#4CAF50', label: 'Completed'};
      case 'rejected':
        return {bg: '#FFEBEE', color: '#F44336', label: 'Rejected'};
      case 'incomplete':
        return {bg: '#FBE9E7', color: '#FF5722', label: 'Incomplete'};
      case 'cancelled':
        return {bg: '#F3F4F6', color: '#6B7280', label: 'Cancelled'};
      case 'pending_approval':
      case 'pending approval':
      default:
        return {bg: '#FFF3E0', color: '#FF9800', label: 'Pending Approval'};
    }
  };

  const getCountdown = (endDateString: string | null | undefined, status: string) => {
    if (!endDateString) return null;
    if (!['approved', 'in_progress', 'in progress', 'on_hold', 'on hold'].includes(status?.toLowerCase())) return null;
    return (
      <CountdownTimer
        endDateString={endDateString}
        status={status}
        overduLabel={t.taskList.overdue}
      />
    );
  };

  const getScoreConfig = (color: string) => {
    switch (color) {
      case 'green': return { bg: '#EBF9F1', color: '#27AE60' };
      case 'yellow': return { bg: '#FEF3C7', color: '#D97706' };
      case 'red': return { bg: '#FEE2E2', color: '#EF4444' };
      default: return { bg: '#F3F4F6', color: '#6B7280' };
    }
  };

  const renderTask = (task: Task): JSX.Element => {
    const statusConfig = getStatusConfig(task.status);
    const isUnread = task.is_new;
    const isUpdated = !task.is_new && task.has_update;

    return (
      <div
        key={task.id}
        style={{
          backgroundColor: theme.colors.white,
          borderRadius: 16,
          padding: 16,
          marginBottom: 10,
          boxShadow: task.has_new_comments ? '0px 4px 20px rgba(245, 158, 11, 0.15)' : '0px 2px 12px rgba(18, 26, 51, 0.09)',
          cursor: 'pointer',
          border: task.has_new_comments ? '1px solid #F59E0B' : '1px solid #F4F6F8',
          position: 'relative',
          overflow: 'hidden',
          animation: task.has_new_comments ? 'highlightPulse 1.5s ease-out infinite' : 'none'
        }}
        onClick={() => {
          navigatedToTaskRef.current = true; // unmount cleanup harus skip overwrite
          const el = document.getElementById('scrollableDiv');
          const updatedItems = tasks.map(t => t.id === task.id ? {...t, is_new: false, has_update: false, has_new_comments: false} : t);
          listCache.save('tasks', {
            items: updatedItems,
            cursor: page,
            hasMore,
            scrollTop: el?.scrollTop || 0,
            meta: { statusFilter, searchTerm: searchTermRef.current, userId: authSlice.userId },
            needsRefresh: true,
          });
          markAsRead(task.id);
          navigate('/TaskDetails', {state: {task, scrollToComments: task.has_new_comments}});
        }}
      >
        {(isUnread || isUpdated) && (
          <div style={{
            position: 'absolute',
            top: 12,
            right: -32,
            backgroundColor: isUnread ? '#FF3B30' : '#2563EB',
            width: 100,
            textAlign: 'center',
            transform: 'rotate(45deg)',
            zIndex: 100,
            padding: '4px 0',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <span style={{color: '#FFFFFF', fontSize: 10, fontWeight: 'bold', lineHeight: 1}}>
              {isUnread ? t.taskList.new : (t.taskList as any).update}
            </span>
          </div>
        )}

        <div style={{...utils.rowCenterSpcBtw(), marginBottom: 12, paddingRight: 5}}>
          <div style={{display: 'flex', flexDirection: 'row', gap: '8px', flexWrap: 'wrap'}}>
            <div style={{ padding: '2px 8px', backgroundColor: '#EBF9F1', borderRadius: 8 }}>
              <text.T14 style={{color: '#27AE60', fontWeight: '600', fontSize: 12, textTransform: 'capitalize'}}>
                {task.task_type}
              </text.T14>
            </div>
            <div style={{ padding: '2px 8px', backgroundColor: statusConfig.bg, borderRadius: 8 }}>
              <text.T14 style={{color: statusConfig.color, fontWeight: '600', fontSize: 12}}>
                {statusConfig.label}
              </text.T14>
            </div>
            {task.score_badge && (
              <div style={{ padding: '2px 8px', backgroundColor: getScoreConfig(task.score_badge.color).bg, borderRadius: 8 }}>
                <text.T14 style={{color: getScoreConfig(task.score_badge.color).color, fontWeight: '600', fontSize: 12}}>
                  Score : {task.score_badge.score}
                </text.T14>
              </div>
            )}
            {task.is_urgent && (
              <div style={{ padding: '2px 8px', backgroundColor: '#FEE2E2', borderRadius: 8 }}>
                <text.T14 style={{color: '#EF4444', fontWeight: '600', fontSize: 12}}>Urgent</text.T14>
              </div>
            )}
            {task.parent_task_id && (
              <div style={{ padding: '2px 8px', backgroundColor: '#EFF6FF', borderRadius: 8 }}>
                <text.T14 style={{color: '#3B82F6', fontWeight: '600', fontSize: 12}}>
                  {t.taskDetails?.followUpTask || 'Follow-up'}
                </text.T14>
              </div>
            )}
            {task.has_new_comments && (
              <div style={{ padding: '2px 8px', backgroundColor: '#FEF3C7', borderRadius: 8 }}>
                <text.T14 style={{color: '#D97706', fontWeight: '600', fontSize: 12}}>
                  {t.taskList.newMessage || '💬 Pesan Baru'}
                </text.T14>
              </div>
            )}
            {task.has_pending_sync && (
              <div style={{ padding: '2px 8px', backgroundColor: '#F0FDF4', borderRadius: 8, border: '1px dashed #86EFAC' }}>
                <text.T14 style={{color: '#16A34A', fontWeight: '600', fontSize: 12}}>⏳ Menunggu sync</text.T14>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <text.T14 style={{fontSize: 12, color: '#9CA3AF'}}>
              <span style={{fontWeight: 600, color: '#6B7280'}}>#{task.id}</span> · {formatDateTime(task.created_at)}
            </text.T14>
            {(task.total_duration ?? 0) > 0 && getCountdown(task.end_date, task.status)}
          </div>
        </div>

        <text.H5 style={{marginBottom: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap'}}>
          {truncateText(task.description, 80)?.replace(/^./, c => c.toUpperCase())}
        </text.H5>

        <div style={{borderTop: '1px solid #F4F6F8', paddingTop: 8, display: 'flex'}}>
          <div style={{display: 'flex', alignItems: 'center', width: '50%'}}>
            <div style={{ width: 35, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
              <svg.UserSvg />
            </div>
            <div>
              <text.T14 style={{fontSize: 11, color: '#9CA3AF'}}>{t.taskDetails.assignedBy}</text.T14>
              <text.T14 style={{fontSize: 13, fontWeight: '600', color: theme.colors.mainDark, textTransform: 'capitalize'}}>
                {abbreviateName(task.assigned_by?.name || 'Unknown')}
              </text.T14>
            </div>
          </div>

          {task.assigned_to && task.assigned_to.length > 0 && (
            <div style={{display: 'flex', alignItems: 'center', width: '50%'}}>
              <div style={{ width: 35, height: 28, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                <svg.UserSvg />
              </div>
              <div style={{flex: 1, minWidth: 0, paddingRight: 10}}>
                <text.T14 style={{fontSize: 11, color: '#9CA3AF'}}>{t.taskDetails.assignedTo}</text.T14>
                <text.T14 style={{fontSize: 13, fontWeight: '600', color: theme.colors.mainDark, textTransform: 'capitalize'}} numberOfLines={1}>
                  {task.assigned_to.length === 1 ? abbreviateName(task.assigned_to[0].name) : `${abbreviateName(task.assigned_to[0].name)} +${task.assigned_to.length - 1}`}
                </text.T14>
              </div>
            </div>
          )}
        </div>

        {(() => {
          const steps = [
            { key: 'draft', label: 'Draft' },
            { key: 'pending_approval', label: 'Pending' },
            { key: 'approved', label: 'Approved' },
            { key: 'in_progress', label: 'Progress' },
            { key: 'in_review', label: 'In Review' },
            { key: 'completed', label: 'Completed' }
          ];

          const currentStatus = task.status?.toLowerCase();
          let currentIndex = steps.findIndex(s => s.key === currentStatus);

          if (currentIndex === -1) {
            if (currentStatus === 'rejected') currentIndex = 4;
            if (currentStatus === 'incomplete') currentIndex = 5;
            if (currentStatus === 'on_hold') currentIndex = 3;
          }

          return (
            <div style={{marginTop: 8, paddingTop: 8, borderTop: '1px solid #F4F6F8'}}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', margin: '0 -8px'}}>
                <div style={{position: 'absolute', top: 5, left: 20, right: 20, height: 2, backgroundColor: '#E0E0E0', zIndex: 0}} />
                <div style={{
                  position: 'absolute', top: 5, left: 20,
                  width: currentIndex <= 0 ? 0 : `calc(${(currentIndex / (steps.length - 1)) * 100}% - ${(currentIndex / (steps.length - 1)) * 40}px)`,
                  height: 2, backgroundColor: theme.colors.mainColor, zIndex: 0,
                  transition: 'width 0.3s ease'
                }} />
                {steps.map((step, index) => {
                  const isActive = index <= currentIndex;
                  const isCurrent = index === currentIndex;
                  return (
                    <div key={step.key} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, width: 40}}>
                      <div style={{
                        width: 12, height: 12, borderRadius: 6,
                        backgroundColor: isActive ? theme.colors.mainColor : '#E0E0E0',
                        border: isCurrent ? `2px solid #E3F2FD` : '1px solid white',
                        boxShadow: isCurrent ? '0 0 0 1px ' + theme.colors.mainColor : 'none',
                        marginBottom: 4,
                      }} />
                      <text.T14 style={{fontSize: 9, textAlign: 'center', color: isActive ? theme.colors.mainDark : '#9CA3AF'}}>
                        {currentStatus === 'incomplete' && index === 4 ? 'Incomplete' : step.label}
                      </text.T14>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <text.T16 style={{textAlign: 'center'}}>{t.taskList.loading}</text.T16>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ flex: 1, padding: '20px 0' }}>
        <div style={{ backgroundColor: '#FFE0E0', color: '#C00000', padding: '10px 15px', borderRadius: 8 }}>
          {error}
        </div>
      </div>
    );
  }

  const filteredTasks = tasks.filter((task) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (task.status || '').toLowerCase() === statusFilter.toLowerCase();
    return matchesStatus;
  }).sort((a, b) => {
    const terminalStatuses = ['completed', 'incomplete', 'cancelled'];
    const aCompleted = terminalStatuses.includes(a.status?.toLowerCase().trim() || '');
    const bCompleted = terminalStatuses.includes(b.status?.toLowerCase().trim() || '');

    if (aCompleted && !bCompleted) return 1;
    if (!aCompleted && bCompleted) return -1;

    if (a.is_urgent && !b.is_urgent) return -1;
    if (!a.is_urgent && b.is_urgent) return 1;

    const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
    const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
    return dateB - dateA;
  });

  return (
    <div
      id="scrollableDiv"
      style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingBottom: 90,
        paddingTop: 4,
        paddingLeft: 8,
        paddingRight: 8,
      }}
    >
      {filteredTasks.length === 0 ? (
        <div style={{textAlign: 'center', marginTop: 40, opacity: 0.5}}>
          <text.T16 style={{marginTop: 10}}>
            {searchTermRef.current ? t.taskList.noTasksFound : t.taskList.noTasksAvailable}
          </text.T16>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={tasks.length}
          next={() => {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchTasks(nextPage, false, debouncedSearchTerm, statusFilter);
          }}
          hasMore={hasMore}
          loader={
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: 10, color: '#9CA3AF'}}>
              <text.T14 style={{textAlign: 'center'}}>{t.taskList.loadingMore}</text.T14>
            </div>
          }
          scrollableTarget="scrollableDiv"
          endMessage={
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: 10, color: '#9CA3AF', marginBottom: 20}}>
              <text.T14 style={{textAlign: 'center'}}>{t.taskList.allDataLoaded}</text.T14>
            </div>
          }
          style={{ overflow: 'visible' }}
        >
          <div style={{marginBottom: 30}}>
            {filteredTasks.map((task) => renderTask(task))}
          </div>
        </InfiniteScroll>
      )}
    </div>
  );
});

// ============================================================
// Task — parent: only manages search/filter UI state.
// All task data lives in TaskListBody — fetch/socket re-renders
// stay isolated there and never dismiss the virtual keyboard.
// ============================================================
export const Task: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const {pathname} = useLocation();
  const { t } = useLanguage();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [taskCounts, setTaskCounts] = useState<Record<string, number>>({});

  // Ref so TaskListBody can always read the latest searchTerm without receiving it as a prop
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  useEffect(() => {
    // Minimal 3 karakter, kecuali pencarian ID task ("#12" / "12")
    if (searchTerm.length > 0 && searchTerm.length < 3 && !/^#?\d+$/.test(searchTerm.trim())) return;
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 700);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setTimeout(() => { window.scrollTo(0, 0); }, 10);
  }, [pathname]);

  const handleCountsUpdate = useCallback((counts: Record<string, number>) => {
    setTaskCounts(counts);
  }, []);

  const handleRestoreFilters = useCallback((search: string, status: string) => {
    setSearchTerm(search);
    setStatusFilter(status);
    setDebouncedSearchTerm(search);
  }, []);

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title={t.taskList.title}
        user={true}
        plus={true}
        onPlus={() => navigate('/CreateTask')}
      />
    );
  };

  const renderSearch = (): JSX.Element => {
    return (
      <div style={{paddingTop: 4, paddingBottom: 8, display: 'flex', alignItems: 'center', gap: 10, position: 'relative', zIndex: 200, flexShrink: 0}}>
        <div style={{flex: 1}}>
          <custom.InputField
            placeholder={t.taskList.searchPlaceholder}
            leftIcon={<svg.SearchSvg />}
            value={searchTerm}
            onChange={(e: any) => setSearchTerm(e.target.value)}
            containerStyle={{marginBottom: 0}}
          />
        </div>
        <div
          style={{
            height: 50,
            width: 50,
            backgroundColor: theme.colors.white,
            borderRadius: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px solid #FFEFE6',
            position: 'relative',
          }}
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
        >
          <FilterListIcon size={24} color={theme.colors.mainDark} />
          {statusFilter !== 'all' && (
            <div
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 8,
                height: 8,
                borderRadius: 4,
                backgroundColor: '#FF3B30',
              }}
            />
          )}
        </div>
        {showFilterDropdown && (
          <>
            <div
              onClick={() => setShowFilterDropdown(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 200 }}
            />
            <div
              style={{
                position: 'absolute',
                top: 60,
                right: 0,
                backgroundColor: 'white',
                borderRadius: 10,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                width: 200,
                padding: 8,
                border: '1px solid #F4F6F8',
                zIndex: 201,
              }}
            >
              {['all', 'draft', 'pending_approval', 'approved', 'in_progress', 'on_hold', 'in_review', 'rejected', 'incomplete', 'completed', 'cancelled'].map(
                (status) => {
                  const count = taskCounts[status] || 0;
                  const showBadge = !['completed', 'incomplete', 'cancelled', 'all'].includes(status);
                  return (
                    <div
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setShowFilterDropdown(false);
                      }}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: statusFilter === status ? theme.colors.mainColor : theme.colors.mainDark,
                        fontWeight: statusFilter === status ? '600' : '400',
                        borderRadius: 8,
                        backgroundColor: statusFilter === status ? '#FFF7F2' : 'transparent',
                        marginBottom: 4,
                      }}
                    >
                      <span style={{textTransform: 'capitalize'}}>
                        {status === 'all' ? t.createTask.checkAll.replace('Check ', '') : (status === 'on_hold' ? 'Pending' : status.replace('_', ' '))}
                      </span>
                      {showBadge && count > 0 && (
                        <div style={{
                          backgroundColor: statusFilter === status ? theme.colors.mainColor : '#F3F4F6',
                          color: statusFilter === status ? 'white' : '#4B5563',
                          padding: '2px 6px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 'bold'
                        }}>
                          {count}
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div id='screen'>
      <style>{`
        ::-webkit-scrollbar {
          width: 0px;
          background: transparent;
          display: none;
        }
        html, body, * {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
        @keyframes blink {
          0% { opacity: 1; }
          50% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes highlightPulse {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
      `}</style>
      {renderHeader()}
      <main
        className='container'
        style={{
          marginTop: 62,
          height: 'calc(100vh - 62px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderSearch()}
        <TaskListBody
          debouncedSearchTerm={debouncedSearchTerm}
          statusFilter={statusFilter}
          searchTermRef={searchTermRef}
          t={t}
          onCountsUpdate={handleCountsUpdate}
          onRestoreFilters={handleRestoreFilters}
        />
      </main>
      <components.BottomTabBar />
    </div>
  );
};
